import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Classes } from '@blueprintjs/core';
import { intersection, uniqBy } from 'lodash';

import { ENTITY_TYPES } from '@kentik/ui-shared/hybrid/constants';
import { Box, Button, Flex, Icon, Text } from 'core/components';
import { Search, VirtualizedTable } from 'core/components/table';
import RouteTableSummaryCollection from 'app/stores/hybrid/RouteTableSummaryCollection';

import { ReactComponent as VPCIcon } from 'app/assets/icons/vpc.svg';
import { ReactComponent as RouterIcon } from 'app/assets/icons/router_icon.svg';
import { ReactComponent as TransitGatewayIcon } from 'app/assets/icons/transit-gateway.svg';
import { ReactComponent as InternetGatewayIcon } from 'app/assets/icons/internet-gateway.svg';
import CloudIcon from '../CloudIcon';

const { SUBNET, FIREWALL } = ENTITY_TYPES.get('aws');

@inject('$hybridMap')
@observer
class CloudSubnetDestinations extends Component {
  collection = new RouteTableSummaryCollection([], {
    sortState: { field: 'destination', direction: 'desc' }
  });

  computeRouteTableSummary = async () => {
    const { subnet, $hybridMap } = this.props;
    const { awsCloudMapCollection, sidebarSettings } = $hybridMap;

    const vpc = awsCloudMapCollection.getEntity({ entityType: 'Vpcs', entityId: subnet.VpcId });
    const vpcTgwAttachmentIds = vpc?.TransitGatewayAttachments?.map((tgwAttachment) => tgwAttachment.id) ?? [];

    const subnetRouteTableSummary = await awsCloudMapCollection.computeSubnetRouteTableSummary(
      subnet.id,
      sidebarSettings.timeRange
    );

    const firewallAdditionalRoute = [];
    for await (const route of subnetRouteTableSummary.routes) {
      if (!route.firewallId || !route.firewallSubnetId) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const firewall = awsCloudMapCollection.topology.Entities[FIREWALL][route.firewallId];
      const firewallSubnet = awsCloudMapCollection.topology.Entities[SUBNET][route.firewallSubnetId];

      const firewallSubnetRouteTableSummary = await awsCloudMapCollection.computeSubnetRouteTableSummary(
        route.firewallSubnetId,
        sidebarSettings.timeRange
      );

      firewallAdditionalRoute.push(
        ...firewallSubnetRouteTableSummary.routes
          .filter((firewallRoute) =>
            awsCloudMapCollection.isInSubnet(firewallRoute.DestinationCidrBlock, route.DestinationCidrBlock)
          )
          .map((firewallRoute) => ({
            ...firewallRoute,
            ...{
              isFirewallRoute: true,
              firewallId: route.firewallId,
              firewallName: firewall?.Name ?? route.firewallId,
              firewallSubnetId: route.firewallSubnetId,
              firewallSubnetName: firewallSubnet.Name
            }
          }))
      );
    }

    const routes = [...subnetRouteTableSummary.routes, ...firewallAdditionalRoute]
      .flatMap((route) => [route, ...(route.moreSpecificRoutes || []).map((r) => ({ ...r, parent: route }))])
      .filter((route) => {
        // always include not specific routes
        if (!route?.routeTgwAttachmentIds) {
          return true;
        }
        // if it a more specific route, we need to make sure that route was generated by tgw in this vpc
        return intersection(route.routeTgwAttachmentIds, vpcTgwAttachmentIds).length > 0;
      });

    const uniqRoutes = uniqBy(
      routes,
      (route) => `${route.destination}-${route.routeTarget?.nextHopResource?.id || route.routeTarget}`
    );

    this.collection.set(uniqRoutes);
    this.collection.filter();
    this.collection.calculatePageCount();
    this.collection.applyPageChangeFilter();
  };

  componentDidMount() {
    const computingPromise = async () => {
      this.collection.setRequestStatus('fetching');
      await this.computeRouteTableSummary();
      this.collection.setRequestStatus(null);
      this.collection.hasFetched = true;
    };

    computingPromise();
  }

  get columns() {
    return [
      {
        label: 'Type',
        name: 'type',
        width: 50,
        renderer: ({ model }) => {
          if (model.get('SegmentName')) {
            return (
              <Box title="Core Network Policy">
                <CloudIcon cloudProvider="aws" entity="coreNetworkPolicy" width={24} height={24} />
              </Box>
            );
          }

          if (model.get('firewallId')) {
            return (
              <Box title="Firewall Route">
                <CloudIcon cloudProvider="aws" entity={FIREWALL} width={24} height={24} />
              </Box>
            );
          }

          return (
            <Box title="Route">
              <CloudIcon cloudProvider="aws" entity="route" width={24} height={24} />
            </Box>
          );
        }
      },
      {
        label: 'Destination',
        name: 'destination',
        width: 160,
        renderer: ({ value }) => (
          <Box>
            <Box>{value}</Box>
          </Box>
        )
      },

      {
        label: 'Target',
        name: 'routeTarget',
        width: 400,
        renderer: ({ value, model }) => {
          // will apply to firewall expanded routes too
          if (model.isTransitGatewayRouteTable) {
            const { attachment, nextHopResource, resourceType } = value;

            return (
              <>
                {model.has('parent') && <Box mb={1}>{model.get('parent.routeTarget')} →</Box>}
                <Box mb={1}>
                  <Text fontWeight="bold">Attachment:</Text>
                  <Flex alignItems="center">
                    <Text as="div" title={attachment.id} ellipsis>
                      {attachment.name}
                    </Text>
                  </Flex>
                </Box>

                <Box>
                  <Text fontWeight="bold">Next Hop Resource:</Text>
                  <Flex alignItems="center">
                    {this.getIcon(resourceType)}
                    {(nextHopResource && (
                      <Text as="div" title={nextHopResource.id} ellipsis>
                        {nextHopResource.name}
                      </Text>
                    )) || (
                      <Text fontStyle="italic" muted>
                        n/a
                      </Text>
                    )}
                  </Flex>
                </Box>
              </>
            );
          }

          if (model.isAwsCoreNetworkRoute) {
            const { $hybridMap } = this.props;
            const { awsCloudMapCollection } = $hybridMap;
            const { VpcId, SubnetId } = model.get('Destinations')?.[0] ?? {};
            const vpc = VpcId ? awsCloudMapCollection.getEntity({ entityType: 'Vpcs', entityId: VpcId }) : null;

            return (
              <>
                {model.has('parent') && <Box mb={1}>{model.get('parent.routeTarget')} →</Box>}
                <Text as="div" mb={1} ellipsis>
                  <Text fontWeight="bold">Segment: </Text>
                  {model.get('SegmentName')} ({model.get('EdgeLocation')})
                </Text>
                <Flex alignItems="center">
                  <Text fontWeight="bold" mr="4px">
                    Next Hop:
                  </Text>
                  {model.get('isAccountNotLoaded') ? (
                    <Flex
                      alignItems="center"
                      minWidth={0}
                      title={`${model.get('VpcIds').join(', ')} in accounts that are not loaded on the map. Add the accounts in the Accounts selector to see their CIDRs and subnets.`}
                    >
                      <Icon icon="warning-sign" color="warning" mr="4px" />
                      <Text color="warning" ellipsis>
                        {model.get('OwnerAccountIds').length === 1 ? 'Account' : 'Accounts'}{' '}
                        {model.get('OwnerAccountIds').join(', ')} not loaded
                      </Text>
                    </Flex>
                  ) : (
                    <>
                      {this.getIcon('vpc')}
                      <Text as="div" title={`${VpcId ?? ''} ${SubnetId ?? value}`} ellipsis>
                        {vpc?.Name || VpcId || value}
                      </Text>
                    </>
                  )}
                </Flex>
              </>
            );
          }

          if (model.isFirewallRoute) {
            return (
              <Flex alignItems="center" flexDirection="column">
                <Box mb={1}>
                  <Text fontWeight="bold">Firewall - {model.get('firewallName')}</Text>
                  <Flex alignItems="center">
                    <Text as="div" title={model.get('value')} ellipsis>
                      {value}
                    </Text>
                  </Flex>
                </Box>
              </Flex>
            );
          }

          // route to an instance or appliance eni, which can be owned by another account
          if (model.get('NetworkInterfaceId')) {
            const { $hybridMap } = this.props;
            const { topology, currentLoadedAccountIds = [] } = $hybridMap.awsCloudMapCollection;
            const eni = topology?.Entities?.NetworkInterfaces?.[value];
            const ownerId = model.get('InstanceOwnerId');
            const isOwnerNotLoaded = !!ownerId && !currentLoadedAccountIds.includes(ownerId);
            const details = ['Network interface', eni?.Description, ownerId && `owner account ${ownerId}`].filter(
              Boolean
            );

            return (
              <Box>
                <Text as="div">{value}</Text>
                {isOwnerNotLoaded ? (
                  <Flex
                    alignItems="center"
                    title={`Account ${ownerId} is not loaded on the map, so details for this interface are unavailable. Add the account in the Accounts selector to see them.`}
                  >
                    <Icon icon="warning-sign" color="warning" mr="4px" />
                    <Text color="warning" small ellipsis>
                      Owner account {ownerId} not loaded
                    </Text>
                  </Flex>
                ) : (
                  <Text as="div" muted small ellipsis title={details.join(' · ')}>
                    {details.join(' · ')}
                  </Text>
                )}
              </Box>
            );
          }

          return (
            <Flex alignItems="center">
              <Text>{value}</Text>
            </Flex>
          );
        }
      }
    ];
  }

  getIcon = (resourceType) => {
    const iconMap = {
      vpc: <Icon icon={VPCIcon} mr="4px" />,
      vpn: <Icon icon={InternetGatewayIcon} mr="4px" />,
      'direct-connect-gateway': <Icon icon={InternetGatewayIcon} mr="4px" />,
      connect: <Icon icon={RouterIcon} mr="4px" />,
      peering: <Icon icon={TransitGatewayIcon} mr="4px" />,
      'tgw-peering': <Icon icon={TransitGatewayIcon} mr="4px" />
    };

    return iconMap[resourceType] || null;
  };

  handleRowClick = (model) => {
    const { onSelect } = this.props;
    onSelect(model.get('destination'));
  };

  handleSearch = (value) => {
    this.collection.filter(value);

    if (!value) {
      this.collection.clearFilters();
      this.collection.resetDiscreteFilters();
    }

    this.collection.calculatePageCount();
    this.changePage(1);

    return this.collection;
  };

  changePage(page) {
    this.collection.setPage(page);
    this.collection.applyPageChangeFilter();
  }

  render() {
    const { pageCount, currentPage } = this.collection.pageState || {};

    return (
      <>
        <Search
          p="4px"
          collection={this.collection}
          onChange={this.handleSearch}
          placeholder="Search..."
          inputProps={{ value: this.collection?.filterState }}
          autoFocus
        />
        <VirtualizedTable
          style={{ height: 320 }}
          collection={this.collection ?? []}
          columns={this.columns}
          rowHeight={({ model }) => (model.isAwsCoreNetworkRoute && model.has('parent') ? 76 : 60)}
          rowClass={Classes.POPOVER_DISMISS}
          onRowClick={this.handleRowClick}
          flexed
        />
        {pageCount > 1 && (
          <Flex justifyContent="space-between" p={1} alignItems="center">
            <Button onClick={() => this.changePage(currentPage - 1)} disabled={currentPage <= 1}>
              Previous
            </Button>
            <Box flexGrow="1" textAlign="center">
              <Text>
                Page {currentPage} of {pageCount}
              </Text>
            </Box>
            <Button onClick={() => this.changePage(currentPage + 1)} disabled={currentPage >= pageCount}>
              Next
            </Button>
          </Flex>
        )}
      </>
    );
  }
}

export default CloudSubnetDestinations;
